import * as React from "react";
import { cn } from "../../lib/utils";
import { Skeleton } from "./Skeleton";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * 로딩 상태를 표시할지 여부
   * @default false
   */
  isLoading?: boolean;

  /**
   * 로딩 시 표시할 스켈레톤 행의 수
   * @default 3
   */
  skeletonRows?: number;

  /**
   * 로딩 시 표시할 스켈레톤 열의 수
   * @default 4
   */
  skeletonCols?: number;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      isLoading = false,
      skeletonRows = 3,
      skeletonCols = 4,
      children,
      ...props
    },
    ref,
  ) => (
    <div className="relative w-full overflow-auto">
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      >
        {children}

        {isLoading && (
          <tbody className="relative">
            {Array.from({ length: skeletonRows }).map((_, rowIndex) => (
              <tr key={`skeleton-row-${rowIndex}`} className="border-b">
                {Array.from({ length: skeletonCols }).map((_, colIndex) => (
                  <td
                    key={`skeleton-cell-${rowIndex}-${colIndex}`}
                    className="p-4"
                  >
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & { isLoading?: boolean }
>(({ className, isLoading, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "[&_tr]:border-b",
      isLoading && "opacity-50 pointer-events-none",
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & { isLoading?: boolean }
>(({ className, isLoading, children, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn(
      "[&_tr:last-child]:border-0",
      isLoading && "hidden",
      className,
    )}
    {...props}
  >
    {!isLoading && children}
  </tbody>
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement> & { isLoading?: boolean }
>(({ className, isLoading, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      isLoading && "opacity-50 pointer-events-none",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("p-4 align-middle [&:has([role=checkbox])]:pr-0", className)}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement> & { isLoading?: boolean }
>(({ className, isLoading, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn(
      "mt-4 text-sm text-muted-foreground",
      isLoading && "opacity-50",
      className,
    )}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

// 스켈레톤 셀 컴포넌트 (내부용)
const TableSkeletonCell = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("h-4 animate-pulse rounded-md bg-muted", className)}
    {...props}
  />
);

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
